import type React from 'react';
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import noop from 'lodash/noop';
import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import log from '@atlassian/jira-common-util-logging/src/log.tsx';
import { useShallowStableReference } from '@atlassian/jira-utils-stable-reference/src/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import {
	type default as FetchError,
	ValidationError,
} from '@atlassian/jira-fetch/src/utils/errors.tsx';
import { isAbortableError } from '@atlassian/jira-fetch/src/utils/is-abortable-error.tsx';
import { isClientFetchError } from '@atlassian/jira-fetch/src/utils/is-error.tsx';
import { performIssuePostRequest } from '@atlassian/jira-issue-reliability-fetch/src/index.tsx';
import { getUpdateAnalyticsFlowHelper } from '@atlassian/jira-issue-analytics/src/services/update-issue-field/index.tsx';
import { getIsEditFromIssueView } from '@atlassian/jira-issue-analytics/src/utils/get-is-edit-from-issue-view/index.tsx';
import { sendExperienceAnalytics } from '@atlassian/jira-issue-view-analytics/src/controllers/send-experience-analytics/index.tsx';
import { getAriConfig, isAri } from '@atlassian/jira-platform-ari/src/index.tsx';
import { STATUS_TYPE } from '@atlassian/jira-platform-field-config/src/index.tsx';
import {
	fireTrackAnalytics,
	fireOperationalAnalytics,
} from '@atlassian/jira-product-analytics-bridge';
import type { StatusDetails } from '@atlassian/jira-issue-shared-types/src/common/types/status-type.tsx';

import { STATUS_FIELD_POST_TRANSITION_KEY } from '../../common/constants.tsx';
import type { StatusValue, StatusTransition } from '../../common/types.tsx';
import { ISSUE_TRANSITION_STATUS_FIELD } from '../../constants.tsx';
import { transitionIssueExperienceDescription } from '../experience-descriptions/index.tsx';
import statusTransitionErrorFlag from '../flags/transition-failed-error/main.tsx';
import statusTransitionValidationErrorFlag from '../flags/validation-error/main.tsx';
import type { Props, State, API } from './types.tsx';

const DIALOG_SUBMIT_BTN = '#issue-workflow-transition-submit';

const safeUnbindDomEvent = (selector: string, event: string, handler: () => void): void => {
	// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
	const el = document.querySelector(selector);
	if (el) {
		el.removeEventListener(event, handler);
	}
};

const safeBindDomEvent = (selector: string, event: string, handler: () => void): void => {
	safeUnbindDomEvent(selector, event, handler);

	// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
	const el = document.querySelector(selector);
	if (el) {
		el.addEventListener(event, handler);
	}
};

const StatusService: React.FC<Props> = ({
	value: propValue,
	initialValue,
	onSubmit = noop,
	onSuccess = noop,
	onFailure = noop,
	onEditCancel = noop,
	children,
	...unstableProps
}) => {
	const props = useShallowStableReference(unstableProps);
	const [stateValue, setStateValue] = useState<StatusDetails>(() => {
		const value = propValue || initialValue;
		if (!value) {
			throw new Error('Either value or initialValue prop is required.');
		}
		return value;
	});
	const [lastSavedValue, setLastSavedValue] = useState<StatusValue>(stateValue);
	const [loading, setLoading] = useState(false);
	const [stateError, setError] = useState<FetchError | null>(null);

	// These are unused but we are keeping parity with the class component
	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	const [isDialogOpened, setIsDialogOpened] = useState(false);

	// These are unused but we are keeping parity with the class component
	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	const [isDialogConfirmPressed, setIsDialogConfirmPressed] = useState(false);

	/**
	 * Equivalent to getDerivedStateFromProps in class component version.
	 *
	 * Lets the consumer control the value of this component. If the consumer passes a `value` prop
	 * it will override the value in state, as long as the component is not in loading state. That's because the component
	 * will only let the consumer know about the new status value once the transition is complete (i.e. loading=false).
	 *
	 * This lifecycle method gets called before every invocation of render().
	 */
	const value = useMemo(() => {
		if (loading || !propValue) {
			return stateValue;
		}
		return propValue;
	}, [loading, propValue, stateValue]);

	// componentDidUpdate
	const prevInitialValueRef = useRef<StatusDetails | undefined>(initialValue);
	useLayoutEffect(() => {
		const prevInitialValue = prevInitialValueRef.current;
		const newInitialValue = initialValue;
		const isNewStatus =
			newInitialValue &&
			prevInitialValue &&
			prevInitialValue.id !== newInitialValue.id &&
			newInitialValue.id !== value.id;
		const hasNameChanged =
			newInitialValue && prevInitialValue && prevInitialValue.name !== newInitialValue.name;
		const hasCategoryChanged =
			newInitialValue &&
			prevInitialValue &&
			prevInitialValue.statusCategory.id !== newInitialValue.statusCategory.id;
		if (isNewStatus || (props.checkRefreshedData && (hasNameChanged || hasCategoryChanged))) {
			setLoading(false);
			setStateValue(newInitialValue);
		}
		prevInitialValueRef.current = newInitialValue;
	}, [initialValue, props.checkRefreshedData, value.id]);

	const onClickDialogConfirm = useCallback(() => {
		setIsDialogConfirmPressed(true);
	}, []);

	const bindDialogFormHandlers = useCallback(() => {
		safeBindDomEvent(DIALOG_SUBMIT_BTN, 'click', onClickDialogConfirm);
	}, [onClickDialogConfirm]);

	const unbindDialogFormHandlers = useCallback(() => {
		safeUnbindDomEvent(DIALOG_SUBMIT_BTN, 'click', onClickDialogConfirm);
	}, [onClickDialogConfirm]);

	useEffect(() => {
		// componentDidMount
		const onDialogContentReady = () => {
			// Using setTimeout as that was the class component version - this may need to be changed for concurrent

			// This event fired by a dialog before the dialog itself is rendered and visible to the user
			// That's why we need to postpone sending the response to the next moment after it became visible
			setTimeout(() => {
				bindDialogFormHandlers();
				setIsDialogOpened(true);
				setIsDialogConfirmPressed(false);
			});
		};

		const handleBeforeDialogHide = () => {
			unbindDialogFormHandlers();
			setIsDialogOpened(false);
			setIsDialogConfirmPressed(false);
		};

		// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
		document.addEventListener('legacyJiraDialogContentReady', onDialogContentReady);
		// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
		document.addEventListener('legacyJiraDialogBeforeHide', handleBeforeDialogHide);

		return () => {
			// componentWillUnmount
			// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
			document.removeEventListener('legacyJiraDialogContentReady', onDialogContentReady);
			// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
			document.removeEventListener('legacyJiraDialogBeforeHide', handleBeforeDialogHide);
			unbindDialogFormHandlers();
			// this.isDialogOpened = false; // we dont need to change the state since we are unmounting
		};
	}, [bindDialogFormHandlers, unbindDialogFormHandlers]);

	const resetError = useCallback(() => {
		setError(null);
	}, []);

	const submit = useCallback(
		(transition: StatusTransition, event: UIAnalyticsEvent) => {
			const oldValue = value;
			const newValue = transition.to;

			const isEditFromIssueView = getIsEditFromIssueView(event);

			const issueAri = props.issueId;
			const issueId = issueAri && isAri(issueAri) ? getAriConfig(issueAri).resourceId : issueAri;

			getUpdateAnalyticsFlowHelper().fireAnalyticsEnd(STATUS_TYPE, {
				analytics: event,
				attributes: {
					fieldType: STATUS_TYPE,
					// Disabling typechange to keep parity
					// eslint-disable-next-line @typescript-eslint/consistent-type-assertions, @typescript-eslint/no-explicit-any
					oldValId: (oldValue as any).statusId ?? oldValue.id,
					newValId: newValue.id,
					oldStatusCategoryId: oldValue.statusCategory.id,
					newStatusCategoryId: newValue.statusCategory.id,
					isInlineEditing: !isEditFromIssueView,
					issueId,
				},
			});

			setLoading(true);
			setStateValue(newValue);
			onSubmit(oldValue, newValue, { transition }, event);
		},
		[value, onSubmit, props.issueId],
	);

	const success = useCallback(
		({
			status,
			event,
			viaDialog = false,
		}: {
			status: StatusValue;
			viaDialog?: boolean;
			event: UIAnalyticsEvent;
		}) => {
			setLoading(false);
			setLastSavedValue(status);
			props.fieldValueActions.setFieldValue(props.issueKey, STATUS_TYPE, status);
			onSuccess(status, viaDialog, event);

			if (fg('fix-status-field-loading-issue')) {
				props.workflowTransitionsActions.setTransitionInProgress(false);
			}

			fireTrackAnalytics(event, 'statusField transitioned', 'statusField');

			sendExperienceAnalytics(
				transitionIssueExperienceDescription(true, 'POST', props.source ?? '', props.projectType),
			);
		},
		[
			onSuccess,
			props.fieldValueActions,
			props.issueKey,
			props.projectType,
			props.source,
			props.workflowTransitionsActions,
		],
	);

	const failure = useCallback(
		(error: FetchError, event: UIAnalyticsEvent) => {
			setError(error);
			setLoading(false);
			setStateValue(lastSavedValue || propValue || initialValue);
			onFailure(error);

			if (fg('fix-status-field-loading-issue')) {
				props.workflowTransitionsActions.setTransitionInProgress(false);
			}

			const isValidationError = error instanceof ValidationError;
			if (isValidationError) {
				props.flagService.showFlag(statusTransitionValidationErrorFlag(error.message));
			} else {
				props.flagService.showFlag(statusTransitionErrorFlag({ error, traceId: error.traceId }));
			}

			fireOperationalAnalytics(event, 'statusField postTransitionFailed', {
				validationError: isValidationError,
				isClientFetchError: isClientFetchError(error),
			});

			sendExperienceAnalytics(
				transitionIssueExperienceDescription(
					false,
					'POST',
					props.source ?? '',
					props.projectType,
					error.message,
					error.traceId,
					error.statusCode,
					isAbortableError(error) ? 'taskAbort' : 'taskFail',
				),
			);
		},
		[
			initialValue,
			lastSavedValue,
			onFailure,
			propValue,
			props.flagService,
			props.projectType,
			props.source,
			props.workflowTransitionsActions,
		],
	);

	const cancel = useCallback(() => {
		setLoading(false);
		// @ts-expect-error - TS2322 - Type 'Status | undefined' is not assignable to type 'Status'.
		setStateValue(propValue || initialValue);
		onEditCancel();
		props.workflowTransitionsActions.setTransitionInProgress(false);
	}, [initialValue, onEditCancel, propValue, props.workflowTransitionsActions]);

	const transitionStatus = useCallback(
		async (transition: StatusTransition, event: UIAnalyticsEvent) => {
			const { appearance, issueKey, renderIssueTransitionModal } = props;
			const newStatus = transition.to;

			submit(transition, event);

			try {
				if (transition.hasScreen) {
					const handleDialogSuccess = () => {
						fireTrackAnalytics(event, 'issueTransition success', {
							triggerPointKey: ISSUE_TRANSITION_STATUS_FIELD,
							isModalOpen: true,
						});

						return success({ status: newStatus, event, viaDialog: true });
					};

					const handleDialogCancel = () => {
						fireTrackAnalytics(event, 'issueTransition cancel', {
							triggerPointKey: ISSUE_TRANSITION_STATUS_FIELD,
							isModalOpen: true,
						});
						return cancel();
					};

					const handleDialogError = () => {
						fireTrackAnalytics(event, 'issueTransition failed', {
							triggerPointKey: ISSUE_TRANSITION_STATUS_FIELD,
							isModalOpen: true,
						});
					};
					if (renderIssueTransitionModal) {
						const { issueId } = props;
						renderIssueTransitionModal({
							payload: {
								issueId,
								issueKey,
								transitionId: transition.id,
							},
							triggerPointKey:
								appearance === 'lozenge'
									? 'issue-transition-status-field-trigger'
									: 'issue-transition-issue-view',
							onDialogSuccess: handleDialogSuccess,
							onDialogCancel: handleDialogCancel,
							onDialogError: handleDialogError,
							analyticsEvent: event,
						});
					}
				} else {
					if (props.onUpdateStatusMutation && fg('relay-migration-issue-fields-status')) {
						await props.onUpdateStatusMutation?.({
							transitionId: transition.id,
							newStatus,
						});
					} else {
						await performIssuePostRequest(
							`/rest/api/2/issue/${issueKey}/transitions`,
							{
								body: JSON.stringify({ transition }),
							},
							'fields/status/src/services/status-service/status-service-new',
						);
					}
					fireTrackAnalytics(event, 'issueTransition success', {
						triggerPointKey: ISSUE_TRANSITION_STATUS_FIELD,
						isModalOpen: false,
					});

					success({ status: newStatus, event });
				}
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
			} catch (error: any) {
				log.safeErrorWithoutCustomerData(STATUS_FIELD_POST_TRANSITION_KEY, error);
				fireTrackAnalytics(event, 'issueTransition failed', {
					triggerPointKey: ISSUE_TRANSITION_STATUS_FIELD,
					isModalOpen: false,
				});
				failure(error, event);
			}
		},
		[cancel, failure, props, submit, success],
	);

	const state: State = useMemo(
		() => ({
			value,
			lastSavedValue,
			loading,
			error: stateError,
		}),
		[value, lastSavedValue, loading, stateError],
	);

	const api: API = useMemo(
		() => ({
			transitionStatus,
			resetError,
		}),
		[resetError, transitionStatus],
	);

	return children({ ...state, ...api });
};

export default StatusService;
